import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AdminPageWrapper } from "../AdminPageWrapper";
import {
  HStack,
  Stack,
  Text,
  Table,
  Spinner,
  ButtonGroup,
  IconButton,
  Button,
} from "@chakra-ui/react";
import { AdminView } from "../../models/AdminView";
import { PageControls } from "../../../pages/map/views/BottomBar/cra-way/PageControls";
import { Page } from "../../../shared/utils/http-utils";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { DeleteIcon, EditIcon } from "../../../shared/views/icons";
import { City } from "../../../survey/models/City";
import { UpdateCityModal } from "./UpdateCityModal";
import { AdminCityApi } from "../../services/AdminCityApi";
import { CustomPopover } from "../../../shared/views/CustomPopover";
import { toast } from "../../../shared/views/CustomToaster";

const pageSize = 20;

const AdminCitiesPageBase: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState<Page<City>>();
  const [editCity, setEditCity] = useState<City>();

  const goToPage = async (pageNumber: number) => {
    setLoading(true);
    const newPage = await AdminCityApi.page(pageSize, pageNumber).then(
      ({ data }) => data,
    );
    setPage(newPage);
    setLoading(false);
  };

  useEffect(() => {
    goToPage(pageIndex);
  }, [pageIndex]);

  const deleteCity = async (cityId: string) => {
    await AdminCityApi.delete(cityId)
      .then(() => {
        toast("success", "City deleted");
        goToPage(pageIndex);
      })
      .catch(() => {
        toast("error", "Deletion failed");
      });
  };

  return (
    <AdminPageWrapper selectedView={AdminView.Cities}>
      <Stack px={6}>
        <Text fontSize="2xl">{AdminView.Cities}</Text>

        <HStack justifyContent={"end"}>
          <PageControls
            pageSize={pageSize}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            nItems={page?.meta?.totalElements ?? 0}
          />
        </HStack>

        {loading ? (
          <Spinner color={"primary.500"} />
        ) : (
          <Table.Root striped>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Country</Table.ColumnHeader>
                <Table.ColumnHeader>Driver side / units</Table.ColumnHeader>
                <Table.ColumnHeader textAlign={"right"}>
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {page?.data.map((city) => (
                <Table.Row key={city.id}>
                  <Table.Cell>{city.name}</Table.Cell>
                  <Table.Cell>{city.country}</Table.Cell>
                  <Table.Cell>
                    {city.rightHandTraffic ? "Right" : "Left"} /{" "}
                    {city.distanceUnit}
                  </Table.Cell>
                  <Table.Cell>
                    <HStack my={-2} justify={"end"}>
                      <ButtonGroup
                        colorPalette={"primary"}
                        variant={"outline"}
                        size="sm"
                        fontSize={"sm"}
                        attached
                      >
                        <IconButton onClick={() => setEditCity(city)}>
                          <EditIcon />
                        </IconButton>
                        <Link to={appRoutes.adminOsmTools(city.id)}>
                          <Button>OSM Tools</Button>
                        </Link>
                      </ButtonGroup>

                      <CustomPopover
                        trigger={
                          <IconButton
                            size="sm"
                            colorPalette={"red"}
                            variant={"outline"}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <Stack p={3}>
                          <Text fontWeight={"semibold"}>Delete city?</Text>
                          <Text fontSize="xs">
                            The call will fail if there are dependencies, so
                            don't worry.
                          </Text>
                          <Button
                            alignSelf={"end"}
                            colorPalette={"red"}
                            onClick={() => deleteCity(city.id)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </CustomPopover>
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>

      {!!editCity && (
        <UpdateCityModal
          city={editCity}
          onClose={() => {
            setEditCity(undefined);
            goToPage(pageIndex);
          }}
        />
      )}
    </AdminPageWrapper>
  );
};

export const AdminCitiesPage = observer(AdminCitiesPageBase);
